import type { RegressionDiagnosis } from '../../interfaces/RegressionDiagnosis';

export interface RegressionEmailContext {
  serviceName: string;
  commitId: string;
  prId: string | null;
  commitTitle: string | null;
  authorName: string | null;
  authorEmail: string | null;
  mergeDate: string | null;
  environmentNames: string[];
  prGuardianUrl: string;
  senderName: string | null;
  senderEmail: string | null;
}

export interface RegressionEmailDraft {
  content: string;
  fileName: string;
}

export interface PullRequestReminderEmailContext {
  prId: number;
  title: string;
  scope: string;
  prUrl: string;
  recipientName: string;
  recipientEmail: string;
  attentionSummary: string;
  ageDays: number | null;
}

export interface BulkPullRequestReminderEmailContext {
  senderName: string;
  reviewers: Array<{
    name: string;
    email?: string | null;
  }>;
  pullRequests: Array<{
    prId: number;
    scope: string;
    prUrl: string;
    attentionSummary: string;
    ageDays: number | null;
  }>;
}

export interface RegressionEmailInlineImage {
  contentId: string;
  fileName: string;
  contentType: 'image/png';
  base64Content: string;
  caption: string;
  altText: string;
}

export class RegressionEmailValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RegressionEmailValidationError';
  }
}

function requiredHeader(value: string | null, label: string): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    throw new RegressionEmailValidationError(`${label} is required to create the email draft.`);
  }
  if (/[\r\n]/.test(trimmed)) {
    throw new RegressionEmailValidationError(`${label} contains invalid characters.`);
  }
  return trimmed;
}

function requiredEmail(value: string | null, label: string): string {
  const email = requiredHeader(value, label);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new RegressionEmailValidationError(`${label} is not a valid email address.`);
  }
  return email;
}

function encodeHeaderValue(value: string): string {
  if (/^[\x20-\x7E]*$/.test(value)) return value;

  const encoder = new TextEncoder();
  const chunks: string[] = [];
  let chunk = '';

  for (const character of value) {
    const candidate = chunk + character;
    if (chunk && encoder.encode(candidate).length > 30) {
      chunks.push(chunk);
      chunk = character;
    } else {
      chunk = candidate;
    }
  }
  if (chunk) chunks.push(chunk);

  return chunks
    .map(part => {
      const bytes = encoder.encode(part);
      const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
      return `=?UTF-8?B?${btoa(binary)}?=`;
    })
    .join(' ');
}

function formatMailbox(displayName: string, email: string): string {
  if (!/^[\x20-\x7E]*$/.test(displayName)) {
    return `${encodeHeaderValue(displayName)} <${email}>`;
  }

  const escapedName = displayName
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
  return `"${escapedName}" <${email}>`;
}

function formatUnresolvedRecipient(displayName: string): string {
  if (!/^[\x20-\x7E]*$/.test(displayName)) {
    return encodeHeaderValue(displayName);
  }

  const escapedName = displayName
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
  return `"${escapedName}"`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;

  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function renderRichText(value: string): string {
  const escaped = renderInlineRichText(value.trim());

  return escaped
    .split(/\n{2,}/)
    .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('\r\n');
}

function renderInlineRichText(value: string): string {
  return escapeHtml(value)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
}

function formatFactor(factor: number): string {
  return `${factor >= 10 ? Math.round(factor) : factor.toFixed(1)}x`;
}

function formatDate(value: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  return slug || 'regression';
}

function validateInlineImage(image: RegressionEmailInlineImage): RegressionEmailInlineImage {
  const contentId = requiredHeader(image.contentId, 'Inline image content ID');
  if (!/^[a-zA-Z0-9._@-]+$/.test(contentId)) {
    throw new RegressionEmailValidationError('Inline image content ID contains invalid characters.');
  }

  const fileName = requiredHeader(image.fileName, 'Inline image file name');
  if (!/^[a-zA-Z0-9._-]+$/.test(fileName)) {
    throw new RegressionEmailValidationError('Inline image file name contains invalid characters.');
  }

  const base64Content = image.base64Content.replace(/\s+/g, '');
  if (!base64Content || !/^[a-zA-Z0-9+/]+={0,2}$/.test(base64Content)) {
    throw new RegressionEmailValidationError('Inline image content is not valid base64 data.');
  }

  return {
    ...image,
    contentId,
    fileName,
    base64Content,
  };
}

function wrapBase64(value: string): string {
  return value.match(/.{1,76}/g)?.join('\r\n') ?? value;
}

function renderRegressionCharts(images: RegressionEmailInlineImage[]): string {
  if (images.length === 0) return '';

  return `
<h4>Regression graphs</h4>
${images.map(image => `<p><b>${escapeHtml(image.caption)}</b></p>
<p><img src="cid:${escapeHtml(image.contentId)}" alt="${escapeHtml(image.altText)}"
style="display:block; max-width:100%; height:auto; border:1px solid #e1e1e1;"></p>`).join('\r\n')}`;
}

function renderMetrics(diagnosis: RegressionDiagnosis): string {
  const metrics = (diagnosis.metrics ?? [])
    .filter(metric => metric.factor !== null && metric.factor > 1.2);

  if (metrics.length === 0) {
    return '<li>PR Guardian identified a confirmed performance regression.</li>';
  }

  return metrics
    .map(metric => {
      const metricName = metric.name.replace(/[\r\n]+/g, ' ');
      return `<li><b>${escapeHtml(metricName)}:</b> approximately ${formatFactor(metric.factor!)} above baseline.</li>`;
    })
    .join('\r\n');
}

function renderCallPath(diagnosis: RegressionDiagnosis): string {
  const frames = diagnosis.callPath ?? [];
  if (frames.length === 0) return '';

  const path = frames
    .map((frame, index) => `${'  '.repeat(index)}${index === 0 ? '' : '-&gt; '}${escapeHtml(frame.name)} (${frame.sharePct.toFixed(1)}%)`)
    .join('\r\n');

  return `
<h4>Observed call path</h4>
<pre>${path}</pre>`;
}

function renderTraceChange(diagnosis: RegressionDiagnosis): string {
  if (diagnosis.traceBefore === null || diagnosis.traceAfter === null) return '';

  const growth = diagnosis.traceGrowth !== null
    ? ` (${formatFactor(diagnosis.traceGrowth)} growth)`
    : '';
  return `<li><b>Trace comparison:</b> ${diagnosis.traceBefore.toFixed(2)} before to ${diagnosis.traceAfter.toFixed(2)} after${growth}.</li>`;
}

function renderSymbols(diagnosis: RegressionDiagnosis): string {
  if (diagnosis.implicatedSymbols.length === 0) return '';

  const symbols = diagnosis.implicatedSymbols
    .map(symbol => `<code>${escapeHtml(symbol)}</code>`)
    .join(', ');
  return `<li><b>Implicated symbols:</b> ${symbols}.</li>`;
}

function renderAgentFix(diagnosis: RegressionDiagnosis): string {
  const agentFix = diagnosis.agentFix;
  if (!agentFix) return '';

  const fixSteps = (agentFix.fixSteps ?? [])
    .filter(Boolean)
    .map(step => `<li>${renderInlineRichText(step)}</li>`)
    .join('\r\n');
  const validation = agentFix.validation
    ? `<p><b>Validation:</b> ${renderInlineRichText(agentFix.validation)}</p>`
    : '';

  if (!fixSteps && !validation) return '';

  return `
<h4>Implementation guidance</h4>
${fixSteps ? `<ul>${fixSteps}</ul>` : ''}
${validation}`;
}

function renderEvidenceLinks(context: RegressionEmailContext, diagnosis: RegressionDiagnosis): string {
  const links = [
    { label: 'PR Guardian report', url: safeHttpUrl(context.prGuardianUrl) },
    { label: 'Aggregated traces / flamegraph', url: safeHttpUrl(diagnosis.aggregatedTracesUrl) },
    { label: 'Code changes', url: safeHttpUrl(diagnosis.codeChangesUrl) },
  ].filter((link): link is { label: string; url: string } => link.url !== null);

  if (links.length === 0) return '';

  return links
    .map(link => `<p class="link"><a href="${escapeHtml(link.url)}">${link.label}</a></p>`)
    .join('\r\n');
}

export function createRegressionEmailDraft(
  context: RegressionEmailContext,
  diagnosis: RegressionDiagnosis,
  generatedAt = new Date(),
  inlineImages: RegressionEmailInlineImage[] = [],
): RegressionEmailDraft {
  if (diagnosis.status !== 'Ready' || diagnosis.verdict !== 'confirmed') {
    throw new RegressionEmailValidationError(
      'A ready, confirmed AI regression diagnosis is required to create an email draft.',
    );
  }

  const senderName = requiredHeader(context.senderName, 'Sender name');
  const senderEmail = requiredEmail(context.senderEmail, 'Sender email');
  const authorName = requiredHeader(context.authorName, 'PR author name');
  const authorEmail = requiredEmail(context.authorEmail, 'PR author email');
  const prId = requiredHeader(context.prId, 'PR ID');
  const commitTitle = requiredHeader(context.commitTitle, 'Commit title');
  const serviceName = requiredHeader(context.serviceName, 'Service name');
  const commitId = requiredHeader(context.commitId, 'Commit ID');

  if (diagnosis.commitId !== commitId || diagnosis.serviceName !== serviceName) {
    throw new RegressionEmailValidationError(
      'The AI diagnosis does not match the selected commit and service.',
    );
  }

  if (!diagnosis.diagnosis?.trim() || !diagnosis.proposedSolution?.trim()) {
    throw new RegressionEmailValidationError(
      'The AI diagnosis and proposed solution are required to create the email draft.',
    );
  }

  const metricNames = (diagnosis.metrics ?? [])
    .filter(metric => metric.factor !== null && metric.factor > 1.2)
    .map(metric => metric.name.replace(/\s*\(.*\)/, '').trim())
    .filter(Boolean);
  const metricLabel = (metricNames.length > 0 ? [...new Set(metricNames)].join(' / ') : 'Performance')
    .replace(/[\r\n]+/g, ' ');
  const environments = context.environmentNames.filter(Boolean);
  const environmentText = environments.length > 0
    ? ` in ${escapeHtml(environments.join(', '))}`
    : '';
  const confidenceText = diagnosis.confidence !== null
    ? ` with ${Math.round(diagnosis.confidence * 100)}% confidence`
    : '';
  const mergeDate = formatDate(context.mergeDate);
  const mergeText = mergeDate ? ` after the ${escapeHtml(mergeDate)} merge` : '';
  const firstName = escapeHtml(authorName.split(/\s+/)[0]);
  const evidenceLinks = renderEvidenceLinks(context, diagnosis);
  const traceChange = renderTraceChange(diagnosis);
  const symbols = renderSymbols(diagnosis);
  const summary = diagnosis.summary?.trim()
    ? renderRichText(diagnosis.summary)
    : '<p>PR Guardian identified a confirmed regression associated with this change.</p>';
  const images = inlineImages.map(validateInlineImage);
  const regressionCharts = renderRegressionCharts(images);

  const subject = `[PR Guardian] ${metricLabel} regression from PR ${prId} - AI diagnosis and suggested fix`;
  const htmlContent = `<html>
<head>
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #201f1e; }
  p, li { font-size: 11pt; }
  h4 { margin: 16px 0 4px 0; font-size: 11pt; }
  pre { font-family: Consolas, monospace; font-size: 10pt; background: #f6f6f6;
        border: 1px solid #e1e1e1; padding: 8px 10px; white-space: pre-wrap; margin: 6px 0; }
  code { font-family: Consolas, monospace; font-size: 10pt; background: #f3f2f1; padding: 0 3px; }
  ul { margin: 4px 0 4px 22px; }
  .link { margin: 0 0 2px 0; }
  .notice { background: #fff4ce; border-left: 4px solid #f7b900; padding: 8px 10px; }
  a { color: #0f6cbd; }
</style>
</head>
<body>

<p>Hi ${firstName},</p>

<p>PR Guardian detected a <b>${escapeHtml(metricLabel)} regression</b> on
${escapeHtml(serviceName)}${environmentText}${mergeText}, attributed to PR ${escapeHtml(prId)}
(<i>${escapeHtml(commitTitle)}</i>). The stored AI diagnosis confirmed the regression${confidenceText}.</p>

${evidenceLinks}

<h4>Regression summary</h4>
${summary}
<ul>
${renderMetrics(diagnosis)}
${traceChange}
${symbols}
  <li><b>Commit:</b> <code>${escapeHtml(commitId)}</code></li>
</ul>

<h4>AI diagnosis</h4>
${renderRichText(diagnosis.diagnosis)}
${renderCallPath(diagnosis)}

<h4>Suggested fix</h4>
${renderRichText(diagnosis.proposedSolution)}
${renderAgentFix(diagnosis)}
${regressionCharts}

<p class="notice"><b>Review required:</b> This draft was generated from stored AI Regression
Diagnoser output and commit metadata. Verify the diagnosis, measurements, recipients, and links before sending.</p>

<p>Thanks,<br>${escapeHtml(senderName)}</p>

</body>
</html>`;

  const messageHeaders = `From: ${formatMailbox(senderName, senderEmail)}
Date: ${generatedAt.toUTCString()}
To: ${formatMailbox(authorName, authorEmail)}
Cc: Guardian project crew <prguardian@microsoft.com>
Subject: ${encodeHeaderValue(subject)}
X-Unsent: 1
MIME-Version: 1.0`;

  let content: string;
  if (images.length === 0) {
    content = `${messageHeaders}
Content-Type: text/html; charset="utf-8"
Content-Transfer-Encoding: 8bit

${htmlContent}`;
  } else {
    const boundary = `----=_PRGuardian_${generatedAt.getTime()}`;
    const imageParts = images.map(image => `--${boundary}
Content-Type: ${image.contentType}; name="${image.fileName}"
Content-Transfer-Encoding: base64
Content-ID: <${image.contentId}>
Content-Disposition: inline; filename="${image.fileName}"

${wrapBase64(image.base64Content)}`).join('\r\n');

    content = `${messageHeaders}
Content-Type: multipart/related; boundary="${boundary}"

--${boundary}
Content-Type: text/html; charset="utf-8"
Content-Transfer-Encoding: 8bit

${htmlContent}
${imageParts ? `\r\n${imageParts}` : ''}
--${boundary}--`;
  }

  return {
    content: content.replace(/\r?\n/g, '\r\n'),
    fileName: `PR${slugify(prId)}-${slugify(commitTitle)}-ai-regression-draft.eml`,
  };
}

export function createPullRequestReminderEmailDraft(
  context: PullRequestReminderEmailContext,
  generatedAt = new Date(),
): RegressionEmailDraft {
  const recipientName = requiredHeader(context.recipientName, 'Recipient name');
  const recipientEmail = requiredEmail(context.recipientEmail, 'Recipient email');
  const title = requiredHeader(context.title, 'PR title');
  const scope = requiredHeader(context.scope, 'Scope');
  const attentionSummary = requiredHeader(context.attentionSummary, 'Attention summary');
  const prUrl = safeHttpUrl(context.prUrl);
  if (!prUrl) {
    throw new RegressionEmailValidationError('PR URL must be an HTTP or HTTPS URL.');
  }

  const subject = `[PR Guardian] Review requested for PR ${context.prId}`;
  const ageText = context.ageDays === null
    ? ''
    : `<p><b>Age:</b> ${context.ageDays} day${context.ageDays === 1 ? '' : 's'}</p>`;
  const html = [
    '<!doctype html>',
    '<html><body>',
    `<p>Hello ${escapeHtml(recipientName)},</p>`,
    `<p>PR <b>${context.prId}</b> is waiting for your review.</p>`,
    `<p><b>Title:</b> ${escapeHtml(title)}</p>`,
    `<p><b>Scope:</b> ${escapeHtml(scope)}</p>`,
    `<p><b>Current status:</b> ${escapeHtml(attentionSummary)}</p>`,
    ageText,
    `<p><a href="${escapeHtml(prUrl)}">Open PR ${context.prId} in Azure DevOps</a></p>`,
    '<p>This is an unsent draft. Please review and edit it before sending.</p>',
    '</body></html>',
  ].join('\r\n');

  return {
    content: [
      `To: ${formatMailbox(recipientName, recipientEmail)}`,
      `Subject: ${encodeHeaderValue(subject)}`,
      `Date: ${generatedAt.toUTCString()}`,
      'MIME-Version: 1.0',
      'Content-Type: text/html; charset=UTF-8',
      'Content-Transfer-Encoding: 8bit',
      'X-Unsent: 1',
      '',
      html,
    ].join('\r\n'),
    fileName: `PR${context.prId}-${slugify(scope)}-review-reminder.eml`,
  };
}

export function createBulkPullRequestReminderEmailDraft(
  context: BulkPullRequestReminderEmailContext,
  generatedAt = new Date(),
): RegressionEmailDraft {
  const senderName = requiredHeader(context.senderName, 'Sender name');
  const reviewers = context.reviewers.map(reviewer => ({
    name: requiredHeader(reviewer.name, 'Reviewer name'),
    email: reviewer.email
      ? requiredEmail(reviewer.email, 'Reviewer email')
      : null,
  })).filter((reviewer, index, items) =>
    items.findIndex(item =>
      item.name.toLowerCase() === reviewer.name.toLowerCase()
      && item.email?.toLowerCase() === reviewer.email?.toLowerCase()) === index);
  if (reviewers.length === 0) {
    throw new RegressionEmailValidationError(
      'At least one reviewer name is required to create the email draft.',
    );
  }

  const pullRequests = context.pullRequests.map(item => {
    const scope = requiredHeader(item.scope, 'Scope');
    const attentionSummary = requiredHeader(item.attentionSummary, 'Attention summary');
    const prUrl = safeHttpUrl(item.prUrl);
    if (!prUrl) {
      throw new RegressionEmailValidationError('PR URL must be an HTTP or HTTPS URL.');
    }
    return { ...item, scope, attentionSummary, prUrl };
  });
  if (pullRequests.length === 0) {
    throw new RegressionEmailValidationError(
      'At least one PR is required to create the email draft.',
    );
  }

  const greeting = reviewers.map(reviewer => reviewer.name).join(', ');
  const recipients = reviewers.map(reviewer =>
    reviewer.email
      ? formatMailbox(reviewer.name, reviewer.email)
      : formatUnresolvedRecipient(reviewer.name));
  const missingEmailCount = reviewers.filter(reviewer => reviewer.email === null).length;
  const subject = `[PR Guardian] ${pullRequests.length} PR${pullRequests.length === 1 ? '' : 's'} waiting for review`;
  const rows = pullRequests.map(item => {
    const age = item.ageDays === null
      ? '-'
      : `${item.ageDays} day${item.ageDays === 1 ? '' : 's'}`;
    return [
      '<tr>',
      `<td><a href="${escapeHtml(item.prUrl)}">${item.prId}</a></td>`,
      `<td>${escapeHtml(item.scope)}</td>`,
      `<td>${escapeHtml(age)}</td>`,
      '</tr>',
    ].join('');
  }).join('\r\n');
  const html = [
    '<!doctype html>',
    '<html><body>',
    `<p>Hello ${escapeHtml(greeting)},</p>`,
    '<p>The following Guardian-created PRs are waiting for your review:</p>',
    '<p>For some context, this is part of the Compute Latency reduction efforts, and these PRs were created based on Azure Profiler CPU traces.</p>',
    '<table border="1" cellpadding="6" cellspacing="0">',
    '<thead><tr><th>PR</th><th>Scope</th><th>Age</th></tr></thead>',
    `<tbody>${rows}</tbody>`,
    '</table>',
    missingEmailCount === reviewers.length
      ? '<p>This is an unsent draft. Add the reviewer email address, then review and edit before sending.</p>'
      : missingEmailCount > 0
        ? '<p>This is an unsent draft. Add the missing reviewer email address, then review and edit before sending.</p>'
        : '<p>This is an unsent draft. Please review and edit it before sending.</p>',
    `<p>Thanks,<br>${escapeHtml(senderName)}</p>`,
    '</body></html>',
  ].join('\r\n');

  return {
    content: [
      `To: ${recipients.join(', ')}`,
      `Subject: ${encodeHeaderValue(subject)}`,
      `Date: ${generatedAt.toUTCString()}`,
      'MIME-Version: 1.0',
      'Content-Type: text/html; charset=UTF-8',
      'Content-Transfer-Encoding: 8bit',
      'X-Unsent: 1',
      '',
      html,
    ].join('\r\n'),
    fileName: pullRequests.length === 1
      ? `PR${pullRequests[0].prId}-${slugify(pullRequests[0].scope)}-review-reminder.eml`
      : `${slugify(reviewers.map(reviewer => reviewer.name).join('-'))}-pr-review-reminder.eml`,
  };
}

export function downloadRegressionEmailDraft(draft: RegressionEmailDraft): void {
  const blob = new Blob([draft.content], { type: 'message/rfc822;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = draft.fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
